import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/ui/components/accordion';
import { ChevronRightIcon } from '@/ui/icons';
import { cn } from '@/ui/lib/utils';
import type { Category } from '../../../common/models/categories/category';
import type { Area } from '../../models/companies/areas/area';
import type { Feature } from '../../models/companies/features/feature';
import type { EcfStage } from '../../models/companies/ecf_stages/ecfStage';
import type { CompanyProperty } from '../../models/companies/company';

interface Props {
  name: string;
  items: readonly (Category | Area | Feature | EcfStage | CompanyProperty)[];
  activeItemId: number | null;
  isOpen: boolean;
  className: string;
  onClick: () => void;
  onClickItem: (id: number | null) => void;
}

// 選択肢の行。Tailwind Preflight を読み込んでいないため、button の
// UA 既定（枠線・背景・フォント）は個別に打ち消す（AccordionTrigger と同じ方針）。
// 区切り線は枠いっぱいに引くため、余白は Panel ではなく行側に持たせる。
const rowClassName = cn(
  'tw:m-0 tw:flex tw:h-14 tw:w-full tw:cursor-pointer tw:items-center tw:justify-between tw:gap-2',
  'tw:border-x-0 tw:border-b-0 tw:border-t tw:border-solid tw:border-border tw:bg-transparent',
  'tw:px-4 tw:text-left tw:text-base tw:font-bold',
  // Panel が overflow-hidden のため、リングは外側でなく内側に描く（DS の FocusedRow story と同じ）
  'tw:outline-none tw:focus-visible:ring-3 tw:focus-visible:ring-inset tw:focus-visible:ring-ring',
);

export function ConditionList({
  name,
  items,
  activeItemId,
  isOpen,
  className,
  onClick,
  onClickItem,
}: Props) {
  return (
    // 開閉状態はページ側が絞り込み条件と一緒に持っているため、値を渡して制御する。
    // リストごとに Root を持つので、他のリストの開閉には影響しない。
    <Accordion
      className={`${className} mb-3`}
      value={isOpen ? [name] : []}
      onValueChange={onClick}
    >
      <AccordionItem value={name}>
        {/* ページの見出しは h1（調達企業一覧）。絞り込みとカード一覧は同列なので
            どちらも h2 に揃える（既定の h3 だとレベルが飛ぶ） */}
        <AccordionTrigger headingLevel="h2">{name}</AccordionTrigger>
        <AccordionContent>
          {items.map(item => (
            <button
              key={item.id}
              type="button"
              className={cn(
                rowClassName,
                activeItemId === item.id
                  ? 'tw:text-primary'
                  : 'tw:text-inherit',
              )}
              onClick={(): void =>
                onClickItem(item.id === activeItemId ? null : item.id)
              }
            >
              {item.name}
              <ChevronRightIcon className="tw:size-6 tw:shrink-0" />
            </button>
          ))}
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
}
